import { useTriggerAction } from '../../hooks';
import { isShowChallenge, selectError, selectMutableWidget, setStatus, store, useAppSelector } from '../../store';
import { getHost } from '../../foundation';
import { EStatus, Routes, TWAction } from '@libs/types';
import React, { useEffect, useEffectEvent, useRef } from 'react';
import { Iframe } from './Iframe';

const showIframeAfter = () => {
    const defaultTimeout = 3000;
    try {
        const evnTimeout = parseInt(process.env.NX_SHOW_CHALLENGE_AFTER ?? `${defaultTimeout}`);
        return Number.isNaN(evnTimeout) ? defaultTimeout : evnTimeout;
    } catch {
        return defaultTimeout;
    }
};

export const ChallengeIframe: React.FC = () => {
    const error = useAppSelector(selectError);
    const statusShowChallenge = useAppSelector(isShowChallenge);
    const widget = useAppSelector((state) => selectMutableWidget(state, error?.widgetUid ?? ''));

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const intervalId = useRef<NodeJS.Timeout | null>(null);
    const showChallengeIntervalId = useRef<NodeJS.Timeout | null>(null);

    const { triggerAction } = useTriggerAction(widget);

    const onTriggerAction = useEffectEvent((action: TWAction) => {
        return triggerAction(action);
    });

    useEffect(() => {
        showChallengeIntervalId.current = setTimeout(() => {
            store.dispatch(setStatus(EStatus.ShowChallenge));
        }, showIframeAfter());
        return () => {
            if (showChallengeIntervalId.current) {
                clearTimeout(showChallengeIntervalId.current);
            }
        };
    }, [error?.captchaBody]);

    useEffect(() => {
        const checkIframeNavigation = () => {
            try {
                if (iframeRef.current && iframeRef.current.contentWindow) {
                    const currentURL = iframeRef.current.contentWindow.location.href;
                    if (error?.actionForRepeat && currentURL.includes(`/${Routes.GET_CAPTCHA_DONE}`)) {
                        if (intervalId.current) {
                            clearInterval(intervalId.current);
                        }
                        onTriggerAction(error.actionForRepeat).catch((e) => console.error(e));
                    }
                }
            } catch {
                console.warn('Cannot access iframe URL due to cross-origin restrictions.');
            }
        };

        intervalId.current = setInterval(checkIframeNavigation, 500);

        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        };
    }, [error?.actionForRepeat]);

    return <Iframe $isFullScreen={statusShowChallenge} ref={iframeRef} src={`${getHost()}${Routes.GET_CAPTCHA}`} title="Security challenge" />;
};
